import { useState } from 'react'
import {
  addContractVersion,
  loadContractHistory,
  type ContractType,
  type ContractVersion,
  type PayBasis,
  type QueuedWrite,
} from '@/data/portal'
import { useAsync } from '@/data/useAsync'
import { Failed, Loading } from '@/components/bits'
import { shortDate, timeOfDay, today } from '@/lib/format'
import type { PersonDetail } from '@/data/queries'
import { useOrg } from '@/org/OrgProvider'

const CONTRACT_TYPES: readonly (readonly [ContractType, string])[] = [
  ['permanent_full_time', 'Permanent, full time'],
  ['permanent_part_time', 'Permanent, part time'],
  ['fixed_term', 'Fixed term'],
  ['casual', 'Casual'],
  ['zero_hours', 'Zero hours'],
  ['seasonal', 'Seasonal'],
  ['apprentice', 'Apprentice'],
  ['agency', 'Agency'],
] as const

const PAY_BASES: readonly (readonly [PayBasis, string])[] = [
  ['hourly', 'Hourly'],
  ['salaried_annual', 'Salaried, annual'],
  ['salaried_hours', 'Salaried against hours'],
  ['unmeasured', 'Unmeasured'],
] as const

function label(pairs: readonly (readonly [string, string])[], value: string): string {
  return pairs.find(([v]) => v === value)?.[1] ?? value
}

function QueueRow({ q }: { q: QueuedWrite }) {
  const future = q.status === 'queued' && q.runAfter > new Date().toISOString()
  const tone =
    q.status === 'succeeded'
      ? 'var(--sev-ok)'
      : q.status === 'failed' || q.status === 'dead'
        ? 'var(--sev-critical)'
        : future
          ? 'var(--sev-due)'
          : 'var(--sev-overdue)'

  const headline =
    q.status === 'succeeded'
      ? 'Written to Planday'
      : q.status === 'dead'
        ? 'Not sent'
        : future
          ? 'Waiting for its effective date'
          : 'Queued for Planday'

  return (
    <div className="qrow">
      <div className="grow">
        <span style={{ color: tone }}>{headline}</span>
        <small>
          {q.hours !== null && `${q.hours} hours a week`}
          {q.effectiveFrom !== null && `, effective ${shortDate(q.effectiveFrom)}`}
          {future && `. Goes out ${shortDate(q.runAfter.slice(0, 10))} at ${timeOfDay(q.runAfter)}.`}
          {q.lastError !== null && ` ${q.lastError}`}
          {q.attempts > 0 && ` (${q.attempts} attempt${q.attempts === 1 ? '' : 's'})`}
        </small>
      </div>
    </div>
  )
}

function Version({
  c,
  previous,
  isCurrent,
}: {
  c: ContractVersion
  previous: ContractVersion | null
  isCurrent: boolean
}) {
  const before = previous?.contractedHoursPerWeek ?? null
  const delta =
    c.contractedHoursPerWeek !== null && before !== null ? c.contractedHoursPerWeek - before : null

  return (
    <div className={isCurrent ? 'tl current' : 'tl'}>
      <div className="when">{shortDate(c.effectiveFrom)}</div>
      <div className="what">
        <div className="headline">
          <span className="figure">
            {c.contractedHoursPerWeek === null ? '—' : `${c.contractedHoursPerWeek}`}
          </span>
          <span style={{ fontSize: 13, color: 'var(--ivory-dim)' }}>hours a week</span>
          {delta !== null && delta !== 0 && (
            <span className={delta > 0 ? 'delta up' : 'delta down'}>
              {delta > 0 ? '+' : ''}
              {delta}
            </span>
          )}
          {isCurrent && <span className="sev due_soon">In force</span>}
          <span className="mono" style={{ fontSize: 12, color: 'var(--ivory-faint)' }}>
            v{c.version}
          </span>
        </div>
        <p>
          {label(CONTRACT_TYPES, c.contractType)} &middot; {label(PAY_BASES, c.payBasis)}
          {c.isIrregularHours && ' · irregular hours'}
          {c.noticePeriodDays !== null && ` · ${c.noticePeriodDays} days notice`}
        </p>
        <div className="meta">
          {c.effectiveTo === null ? 'Open ended' : `Until ${shortDate(c.effectiveTo)}`}
        </div>
      </div>
    </div>
  )
}

export function Hours({ person }: { person: PersonDetail }) {
  const { current } = useOrg()
  const result = useAsync(
    () => loadContractHistory(current.id, person.employment.id),
    [current.id, person.employment.id],
  )

  const [open, setOpen] = useState(false)
  const [hours, setHours] = useState('')
  const [from, setFrom] = useState(today())
  const [type, setType] = useState<ContractType>('permanent_part_time')
  const [basis, setBasis] = useState<PayBasis>('hourly')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (result.state === 'loading') return <Loading what="contracted hours" />
  if (result.state === 'error') return <Failed message={result.message} onRetry={result.reload} />

  const { versions, queue } = result.data
  const now = today()
  const currentIndex = versions.findIndex(
    (c) => c.effectiveFrom <= now && (c.effectiveTo === null || c.effectiveTo >= now),
  )
  const latest = versions[0] ?? null
  const parsed = Number(hours)
  const valid = hours.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 80

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await addContractVersion({
        organisationId: current.id,
        employmentId: person.employment.id,
        previous: latest,
        effectiveFrom: from,
        contractType: type,
        payBasis: basis,
        contractedHoursPerWeek: parsed,
        noticePeriodDays: notice.trim() === '' ? null : Number(notice),
      })
      setOpen(false)
      setHours('')
      result.reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stack">
      <div className="card">
        <div className="head">
          <h2>Contracted hours</h2>
          <div className="spacer" />
          <span className="owner">We own this, we push it</span>
          <button className="btn sm" type="button" onClick={() => setOpen((v) => !v)}>
            {open ? 'Cancel' : 'Change hours'}
          </button>
        </div>

        {open && (
          <div className="addform">
            <h3>New contract version</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ivory-dim)', maxWidth: '66ch' }}>
              A change dated in the future is queued and sent to Planday on the day it takes effect,
              not today, so Planday always matches the contract in force. Changing the hours also
              changes holiday entitlement, which is pro-rated on them.
            </p>
            <div className="grid">
              <label>
                Hours a week
                <input
                  type="number"
                  min="0"
                  max="80"
                  step="0.5"
                  inputMode="decimal"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  placeholder="32.5"
                />
              </label>
              <label>
                Effective from
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label>
                Contract type
                <select value={type} onChange={(e) => setType(e.target.value as ContractType)}>
                  {CONTRACT_TYPES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Pay basis
                <select value={basis} onChange={(e) => setBasis(e.target.value as PayBasis)}>
                  {PAY_BASES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Notice period, days
                <input
                  type="number"
                  min="0"
                  value={notice}
                  onChange={(e) => setNotice(e.target.value)}
                  placeholder="Optional"
                />
              </label>
              <div>
                <button
                  className="btn"
                  type="button"
                  onClick={() => void save()}
                  disabled={busy || !valid}
                >
                  {busy ? 'Saving…' : 'Save version'}
                </button>
              </div>
            </div>
            {error !== null && (
              <div className="callout" style={{ marginTop: 14, borderLeftColor: 'var(--sev-critical)' }}>
                {error}
              </div>
            )}
          </div>
        )}

        <div className="body flush">
          {versions.length === 0 ? (
            <div className="empty">
              <p>
                No contract recorded. Planday holds a figure for contracted hours, but it is not the
                contract: this is where the versioned record belongs.
              </p>
            </div>
          ) : (
            <div className="timeline">
              {versions.map((c, i) => (
                <Version
                  key={c.id}
                  c={c}
                  previous={versions[i + 1] ?? null}
                  isCurrent={i === currentIndex}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="head">
          <h2>Planday write queue</h2>
        </div>
        <div className="body flush">
          {queue.length === 0 ? (
            <div className="empty">
              <p>Nothing queued for this person.</p>
            </div>
          ) : (
            <div className="queue">
              {queue.map((q) => (
                <QueueRow key={q.outboxId} q={q} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
